import NavBar from "@layouts/public/NavBar";
import useHappyFox from "@modules/happyfox/useHappyFox";
import {
  ArrowLeftIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@talladega/icons";
import {
  formatCurrency,
  dateTimeFormatter,
  gtmPushData,
} from "@talladega/helpers";
import CoreLayout from "@layouts/core/CoreLayout";
import PurchaseStepper from "@modules/purchase/warranty/components/PurchaseStepper";
import {
  getAllPurchaseStepsForFlow,
  getPreviousStep,
  getStepById,
  aftermarketDetailsStep,
  PurchaseStep,
  stepIsBeforeSigning,
  purchaseLastStep,
  aftermarketQuoteStep,
} from "@modules/purchase/PurchaseStep";
import classNames from "classnames";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import { MouseEvent, ReactNode, useEffect, useMemo, useState } from "react";
import PurchaseInformation from "@modules/purchase/warranty/components/PurchaseInformation";
import {
  Purchase,
  PurchasePreview,
} from "@modules/purchase/warranty/types/Purchase";
import { Transition } from "@headlessui/react";
import PurchaseIntroInformation from "@modules/purchase/warranty/components/PurchaseIntroInformation";
import useUser from "@modules/user/hooks/useUser";
import LoginFlowWrapper from "@components/LoginFlowWrapper";
import { loginMode, LoginModalMode } from "@modules/user/types/LoginModalMode";
import CoverageOverwriteModal from "@modules/purchase/warranty/components/coverage/CoverageOverwriteModal";
import useSetActiveAftermarketPurchaseMutation from "@modules/purchase/warranty/hooks/useSetActiveAftermarketPurchaseMutation";
import useAftermarketPurchase from "@modules/purchase/warranty/hooks/useAftermarketPurchase";
import { useQueryClient } from "@tanstack/react-query";
import purchaseQueryKeys from "@modules/purchase/purchaseQueryKeys";
import { LoadingSpinner, Button, Modal } from "@talladega/components";
import PurchaseSourceVehicleImage from "@common/components/PurchaseSourceVehicleImage";
import CoverageDetailsModal from "@modules/purchase/warranty/components/CoverageDetailsModal";
import useGetDealLink from "@modules/purchase/warranty/hooks/useGetDealLink";
import ConsumerPurchaseLayoutContext, {
  ConsumerPurchaseLayoutContextState,
} from "@modules/purchase/ConsumerPurchaseLayoutContext";
import { applyPromoCode } from "@modules/purchase/warranty/hooks/useAftermarketApplyPromoCodeMutation";
import { removeQueryParams } from "@modules/core/routing/routingHelpers";
import PromoCodeAppliedModal from "@modules/purchase/warranty/components/PromoCodeAppliedModal";
import { convert, LocalDate } from "@js-joda/core";
import DealCustomerTypeEnum from "@common/enums/DealCustomerTypeEnum";
import { getProvinceById } from "@talladega/types";
import { sources } from "@common/enums/SourceEnum";
import WarrantyResumePurchaseMobileFloater from "@modules/purchase/warranty/components/WarrantyResumePurchaseMobileFloater";
import RoadwiseNavBar from "./public/RoadwiseNavBar";

type Props = {
  children: ReactNode;
  currentStep?: PurchaseStep;
  showBackButton?: boolean;
  purchase?: Purchase;
  purchasePreview?: PurchasePreview;
  removePadding?: boolean;
  onLoginSuccess?: () => void;
  onLoginClose?: () => void;
  headerTitle?: string;
  headerSubtitle?: string;
  isOnIndex?: boolean;
  isOnLanding?: boolean;
  isOnComplete?: boolean;
  autoRedirectToPurchaseAfterLogin?: boolean;
  showResumePurchase?: boolean;
  purchasePastQuote?: boolean;
  consumerFinancingEnabled?: boolean;
};

type ChildrenContainerProps = {
  children: ReactNode;
  purchase?: Purchase;
  purchasePreview?: PurchasePreview;
  currentStep?: PurchaseStep;
  removePadding?: boolean;
  isOnIndex?: boolean;
};

const ChildrenContainer = ({
  children,
  purchase,
  purchasePreview,
  currentStep,
  removePadding = false,
  isOnIndex = false,
}: ChildrenContainerProps) => {
  if (currentStep?.isPointOfNoReturn) {
    return <div>{children}</div>;
  }

  return (
    <div
      className={`overflow-hidden lg:min-h-[calc(100vh-148px)] xl:min-h-[calc(100vh-92px)] ${
        isOnIndex ? "md:flex" : "flex"
      }`}
    >
      <div
        className={classNames("flex-auto", {
          "w-4/6 px-4 py-8 lg:px-12 xl:px-28 xl:py-12": !removePadding,
        })}
      >
        {children}
      </div>
      {currentStep && (
        <div className="hidden w-1/3 bg-secondary lg:block">
          <div className="sticky top-12">
            {purchase && purchase.vehicle && (
              <PurchaseInformation
                purchasePreview={purchasePreview}
                purchase={purchase}
              />
            )}
            {!purchase?.vehicle && <PurchaseIntroInformation />}
          </div>
        </div>
      )}
    </div>
  );
};

const shouldShowReSignModal = (
  previousStep: PurchaseStep,
  currentStep: PurchaseStep,
  anyDocumentIsSigned: boolean
) => {
  return (
    previousStep &&
    currentStep &&
    anyDocumentIsSigned &&
    stepIsBeforeSigning(previousStep) &&
    !stepIsBeforeSigning(currentStep)
  );
};

const CoveragePurchaseLayout = ({
  children,
  currentStep,
  showBackButton = true,
  purchase,
  purchasePreview,
  removePadding,
  onLoginSuccess,
  onLoginClose,
  headerTitle,
  headerSubtitle,
  isOnIndex = false,
  isOnLanding = false,
  autoRedirectToPurchaseAfterLogin: autoRedirectAfterLogin = true,
  showResumePurchase = false,
  isOnComplete = false,
  purchasePastQuote = false,
  consumerFinancingEnabled,
}: Props) => {
  useHappyFox();

  const { user } = useUser();
  const { t } = useTranslation(["purchase", "common"]);
  const router = useRouter();
  const locale = router?.locale || "en";
  const { source, login } = router.query;
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);

  const email = router.query.email as string | undefined;
  const confirm = router.query.confirm as string | undefined;
  const isResetLink = !!email && !!router.query.token && !confirm; // email+token without confirm
  // Only treat token as a *deal* token when NOT a reset-password flow
  const dealToken = !isResetLink
    ? (router.query.token as string | undefined)
    : undefined;

  const roadwiseRedirect = router.query.roadwiseRedirect === "true";

  const { data } = useGetDealLink(dealToken);

  useEffect(() => {
    if (user?.isLoggedIn) return;
    if (isResetLink) {
      setShowLoginModal(false);
      return;
    }
    setShowLoginModal(
      (login !== undefined && login === "true") ||
        (data !== undefined && data.type === "login")
    );
  }, [setShowLoginModal, login, user, data, isResetLink]);

  const previousStep =
    showBackButton && currentStep
      ? getPreviousStep("aftermarket", currentStep)
      : undefined;
  const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [showCoverageDetailsModal, setShowCoverageDetailsModal] =
    useState(false);
  const consumerPurchaseLayoutContextState: ConsumerPurchaseLayoutContextState =
    useMemo(
      () => ({
        showCoverageDetailsModalState: [
          showCoverageDetailsModal,
          setShowCoverageDetailsModal,
        ],
      }),
      [showCoverageDetailsModal]
    );
  const {
    isFetching: isPurchaseFetching,
    isStale: isPurchaseStale,
    refetch: refetchPurchase,
  } = useAftermarketPurchase();
  const setActiveAftermarket = useSetActiveAftermarketPurchaseMutation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isPurchaseFetching || !purchase || !purchase?.sourceInfo) return;

    // Sets the GTM data layer for source params.
    const { appId, sessionId, adId, userId, locationId, ctaId } =
      purchase.sourceInfo;
    gtmPushData({
      source: purchase.dealSource,
      sourceAppId: appId || "",
      sourceSessionId: sessionId || "",
      sourceParamIds: `Ad ID: ${adId || ""}, User ID: ${
        userId || ""
      }, Location ID: ${locationId || ""}, CTA ID: ${ctaId || ""}`,
    });
  }, [isPurchaseFetching, purchase]);

  const { promo } = router.query;
  const [promoCodeName, setPromoCodeName] = useState("");
  const [showPromoCodeApplied, setShowPromoCodeApplied] =
    useState<boolean>(false);

  useEffect(() => {
    if (promo && router.isReady && !isPurchaseFetching) {
      setPromoCodeName(promo as string);
      if (purchase) {
        // Directly calling the applyPromoCode function to ensure it runs synchronously.
        applyPromoCode({
          dealId: purchase.id,
          promoCode: promo as string,
        }).then(
          () => {
            removeQueryParams(router, ["promo"]);
            setShowPromoCodeApplied(true);
          },
          () => {
            removeQueryParams(router, ["promo"]);
          }
        );
      } else {
        setShowPromoCodeApplied(true);
        removeQueryParams(router, ["promo"]);
      }
    }
  }, [isPurchaseFetching, promo, purchase, refetchPurchase, router]);

  useEffect(() => {
    // wait until purchase is not stale
    const { loginSuccessful } = router.query;

    if (
      isPurchaseStale ||
      isPurchaseFetching ||
      (!purchase?.currentStepId &&
        !purchase?.overwriteCandidate?.currentStepId &&
        !loginSuccessful)
    )
      return;

    if (purchase?.currentStepId || purchase?.overwriteCandidate) {
      if (
        purchase?.overwriteCandidate &&
        router.pathname !== purchaseLastStep.aftermarket.href
      ) {
        setShowOverwriteModal(true);
      } else if (
        (purchase?.currentStepId &&
          loginSuccessful === "true" &&
          autoRedirectAfterLogin) ||
        (purchase?.currentStepId && dealToken)
      ) {
        // does not have an overwrite candidate, but the user just logged in
        const purchaseStep = getStepById("aftermarket", purchase.currentStepId);

        if (purchaseStep && router.pathname !== purchaseStep.href) {
          router.push({
            pathname: purchaseStep.href,
          });
        } else {
          const mainApplicant = purchase.customers.find(
            (c) => c.typeId === DealCustomerTypeEnum.MainApplicant
          );

          const provinceAbbr = mainApplicant?.currentAddress?.provinceId
            ? getProvinceById(mainApplicant.currentAddress.provinceId)?.abbr
            : "";

          const odometer = purchase.vehicle?.odometerCount;
          const vin = purchase.vehicle?.vin;
          const year = purchase.vehicle?.year;
          const make = purchase.vehicle?.make;
          const model = purchase.vehicle?.model;

          const hasValidVehicleInfo = !!vin || (!!year && !!make && !!model);

          const hasProvince = !!provinceAbbr;
          const hasOdometer = !!odometer;

          if (hasValidVehicleInfo && hasProvince && hasOdometer) {
            const quoteParams: Record<string, string> = {
              source:
                sources
                  .find((s) => s.name === purchase.dealSource.toString())
                  ?.name.toLowerCase() || "",
              odometer: odometer.toString(),
              province: provinceAbbr,
              removeDiscount: "true",
            };

            if (vin) {
              quoteParams.vin = vin;
            } else {
              quoteParams.year = year?.toString() || "";
              quoteParams.make = make || "";
              quoteParams.model = model || "";
              quoteParams.trim = purchase.vehicle?.trim || "";
            }

            if (!purchasePastQuote) {
              const { href } = aftermarketQuoteStep;
              router.push(
                `${href}?${new URLSearchParams(quoteParams).toString()}`
              );
            }
          }
        }
      }
    }

    if (loginSuccessful) {
      setIsPageLoading(false);
      removeQueryParams(router, ["loginSuccessful"]);
    }
  }, [
    autoRedirectAfterLogin,
    isPurchaseFetching,
    isPurchaseStale,
    purchase,
    purchasePastQuote,
    roadwiseRedirect,
    router,
    source,
    dealToken,
  ]);

  const onLoginModalClose = () => {
    if (onLoginClose) {
      onLoginClose();
    }
    setShowLoginModal(false);
  };

  const onLogin = async () => {
    setIsPageLoading(true);

    queryClient.invalidateQueries({
      queryKey: purchaseQueryKeys.activeAftermarketPurchase(),
    });

    onLoginSuccess?.();
  };

  const [loginModalMode, setLoginModalMode] =
    useState<LoginModalMode>(loginMode);

  const redirectToCurrentStep = (currentStepId: number | null) => {
    setShowOverwriteModal(false);

    const purchaseStep = getStepById(
      "aftermarket",
      currentStepId ?? aftermarketDetailsStep.id
    );

    if (purchaseStep) {
      router.push({
        pathname: purchaseStep.href,
      });
    }
  };

  const handleOnResumePurchaseClick = () => {
    if (!purchase) return;

    if (purchase.currentStepId === purchaseLastStep.aftermarket.id) {
      redirectToCurrentStep(purchase.currentStepId);
    } else {
      setActiveAftermarket.mutate(purchase.id, {
        onSuccess: () => {
          redirectToCurrentStep(purchase.currentStepId);
        },
      });
    }
  };

  const handleOnOverwritePurchaseClick = () => {
    if (!purchase?.overwriteCandidate) return;

    setActiveAftermarket.mutate(purchase.overwriteCandidate.id, {
      onSuccess: () =>
        redirectToCurrentStep(purchase.overwriteCandidate.currentStepId),
    });
  };

  const handleBackClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      previousStep &&
      currentStep &&
      purchase &&
      shouldShowReSignModal(
        previousStep,
        currentStep,
        purchase.documentSummary.anyDocumentIsSigned
      )
    ) {
      event.preventDefault();
      setShowModal(true);
    }
  };

  const pricing = purchasePreview?.pricing ?? purchase?.pricing;

  const isOnAftermarketDetailsStep =
    router.pathname === aftermarketDetailsStep.href;

  return (
    <CoreLayout>
      <LoginFlowWrapper
        isLoginModalOpen={showLoginModal}
        onLoginModalClose={onLoginModalClose}
        onLogin={onLogin}
        onEmailConfirmed={onLogin}
        loginModalMode={loginModalMode}
        socialLoginParams="loginSuccessful=true"
      >
        {consumerFinancingEnabled ? (
          <RoadwiseNavBar
            user={user}
            setShowLoginModal={setShowLoginModal}
            setLoginModalMode={setLoginModalMode}
            mode={isOnLanding || isOnComplete ? "dark" : "light"}
          />
        ) : (
          <NavBar
            user={user}
            setShowLoginModal={setShowLoginModal}
            setLoginModalMode={setLoginModalMode}
            showResumePurchase={showResumePurchase}
            mode={isOnLanding || isOnComplete ? "dark" : "light"}
          />
        )}

        <div className="bg-white">
          <ConsumerPurchaseLayoutContext.Provider
            value={consumerPurchaseLayoutContextState}
          >
            {!isOnLanding && purchase?.vehicle && (
              <div
                className={classNames(
                  "sticky top-0 z-50 bg-secondary p-4 lg:hidden",
                  {
                    hidden: currentStep?.isPointOfNoReturn,
                  }
                )}
              >
                {!currentStep?.isPointOfNoReturn && (
                  <div className="flex items-center justify-between">
                    <div
                      role="presentation"
                      className="text-md flex-1 font-semibold hover:cursor-pointer"
                      onClick={() => setIsOrderSummaryOpen((prev) => !prev)}
                    >
                      <div className="flex gap-3 sm:gap-6">
                        {purchase.vehicle.image && (
                          <PurchaseSourceVehicleImage
                            className="max-h-24 w-1/3 rounded-xl object-cover md:w-1/4"
                            src={purchase.vehicle.image}
                            alt="Vehicle picture for purchase"
                          />
                        )}

                        {purchase.vehicle &&
                          purchase.vehicle.year &&
                          purchase.vehicle.make &&
                          purchase.vehicle.model && (
                            <div className="flex w-full flex-row items-center gap-2 sm:gap-6">
                              <div className="heading-3 w-fit">
                                <p className="font-semibold">
                                  {purchase.vehicle.year}&nbsp;
                                  {purchase.vehicle.make}&nbsp;
                                  {purchase.vehicle.model}
                                </p>

                                {purchase.vehicle.trim && (
                                  <span className="mb-3 text-gray-500">
                                    {purchase.vehicle.trim}
                                  </span>
                                )}
                                {purchase.vehicle.odometerCount > 0 && (
                                  <span className="text-gray-500">
                                    {purchase.vehicle.trim ? " | " : ""}
                                    {purchase.vehicle.odometerCount.toLocaleString()}{" "}
                                    {t("common:km")}
                                  </span>
                                )}

                                {!isOnAftermarketDetailsStep &&
                                  pricing &&
                                  pricing.paymentFrequencyId == null &&
                                  pricing.totalPrice > 0 && (
                                    <p>
                                      {formatCurrency(
                                        pricing.totalPrice,
                                        locale || "en"
                                      )}
                                    </p>
                                  )}
                                {!isOnAftermarketDetailsStep &&
                                  pricing &&
                                  pricing.paymentFrequencyId != null &&
                                  pricing.payment > 0 && (
                                    <>
                                      <p>
                                        {t("purchase:per_month_amount", {
                                          amount: formatCurrency(
                                            pricing.payment,
                                            locale || "en"
                                          ),
                                        })}
                                      </p>
                                      <p className="text-sm font-semibold leading-5 text-gray-600">
                                        {t("purchase:payments_starting_date", {
                                          term: pricing.term ?? 0,
                                          date: dateTimeFormatter(
                                            convert(
                                              LocalDate.now().plusMonths(1)
                                            ).toDate(),
                                            locale ?? "en",
                                            "MMM D, YYYY"
                                          ),
                                        })}
                                      </p>
                                    </>
                                  )}
                              </div>
                            </div>
                          )}
                        <div className="flex items-center">
                          {!isOrderSummaryOpen && (
                            <ChevronDownIcon className="ml-2 inline" />
                          )}
                          {isOrderSummaryOpen && (
                            <ChevronUpIcon className="ml-2 inline" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <Transition
                  show={isOrderSummaryOpen}
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0"
                >
                  <div className="flex flex-col justify-center bg-secondary pt-4">
                    <PurchaseInformation
                      purchasePreview={purchasePreview}
                      purchase={purchase}
                    />

                    <Button
                      fill="link"
                      leftIcon={<ArrowUpIcon className="h-4" />}
                      onClick={() => setIsOrderSummaryOpen(false)}
                    >
                      {t("common:show_less")}
                    </Button>
                  </div>
                </Transition>
              </div>
            )}
            <ChildrenContainer
              currentStep={currentStep}
              purchase={purchase}
              purchasePreview={purchasePreview}
              removePadding={removePadding}
              isOnIndex={isOnIndex}
            >
              {currentStep && !currentStep.isPointOfNoReturn && (
                <PurchaseStepper
                  steps={getAllPurchaseStepsForFlow("aftermarket").filter(
                    (s) => !s.isPointOfNoReturn
                  )}
                  currentStep={currentStep}
                />
              )}
              {headerTitle && headerSubtitle && (
                <>
                  <h1 className="mb-2.5 text-3xl">{headerTitle}</h1>
                  <p className="text-lg text-secondary">{headerSubtitle}</p>
                </>
              )}
              {previousStep && (
                <div className="mx-6 my-8 ml-2 items-center flex">
                  <Link
                    className="text-md flex gap-x-2 font-semibold text-primary-500"
                    href={{
                      pathname: previousStep.href,
                    }}
                    onClick={handleBackClick}
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                    <span>
                      {t("common:back")}{" "}
                      {previousStep
                        ? `${t("common:to")} ${t(previousStep.nameTranslation)}`
                        : ""}
                    </span>
                  </Link>
                </div>
              )}
              {children}
            </ChildrenContainer>
          </ConsumerPurchaseLayoutContext.Provider>
        </div>
      </LoginFlowWrapper>
      {purchase && (
        <CoverageOverwriteModal
          purchase={purchase}
          show={showOverwriteModal}
          onClose={() => setShowOverwriteModal(false)}
          onResumePurchaseClick={handleOnResumePurchaseClick}
          onOverwritePurchaseClick={handleOnOverwritePurchaseClick}
          disableClosing
        />
      )}

      <Modal
        isOpen={showModal}
        alignment="center"
        onClose={() => {
          setShowModal(false);
        }}
        title={t("purchase:update_contracts")}
      >
        <h5 className="mb-3 text-primary">
          {t("purchase:do_you_want_to_update_contracts")}
        </h5>
        <p>{t("purchase:if_change_sign_again")}</p>
        <Modal.Buttons
          primaryButtonText={t("purchase:i_want_update_contract")}
          onPrimaryClick={() => {
            if (previousStep) {
              router.push({
                pathname: previousStep.href,
              });
            }
          }}
          secondaryButtonText={t("common:cancel")}
          secondaryButtonFill="link"
          onSecondaryClick={() => {
            setShowModal(false);
          }}
          stretchButtonToContent
        />
      </Modal>

      {showCoverageDetailsModal && purchase && (
        <CoverageDetailsModal
          purchase={purchase}
          isOpen={showCoverageDetailsModal}
          onClose={() => setShowCoverageDetailsModal(false)}
        />
      )}

      {showPromoCodeApplied && (
        <PromoCodeAppliedModal
          promoCode={promoCodeName}
          isOpen={showPromoCodeApplied}
          onClose={() => {
            setShowPromoCodeApplied(false);
            refetchPurchase();
          }}
        />
      )}

      <LoadingSpinner isLoading={isPageLoading} isOverlay />
      <WarrantyResumePurchaseMobileFloater
        showResumePurchase={showResumePurchase}
      />
    </CoreLayout>
  );
};

export default CoveragePurchaseLayout;
